import React, { useEffect, useId, useRef, useState } from "react";
import { FileJson2, RefreshCw, Wrench } from "lucide-react";
import { CompactButton } from "../Button/Buttons";
import FileUploadInput from "../Inputs/FileUploadInput";
import Input from "../Inputs/Input";
import Select from "../Inputs/Select";
import AlertBanner from "../shared/AlertBanner/AlertBanner";
import InlineSpinner from "../shared/Spinner/InlineSpinner";
import Tabs from "../shared/Tabs";
import logger from "../../services/Logger";
import { convertHTTPAPISchema } from "../../api/providers";
import JsonView from "./DualViewEditor/JsonView";

export const MAX_SCHEMA_FILE_SIZE = 1024 * 1024;
export const EMPTY_HTTP_API_DRAFT = {
  baseURI: "",
  schema: "",
  format: "json",
  convertedDraft: null,
};

const DOCUMENT_TABS = [
  { value: "schema", label: "API Schema", Icon: FileJson2 },
  { value: "tools", label: "Generated MCP Tools", Icon: Wrench },
];
const FORMATS = [
  { value: "json", label: "JSON" },
  { value: "yaml", label: "YAML" },
];
const LABEL_CLASS = "block text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300 mb-1.5";

const getURIError = (value) => {
  if (!value.trim()) return null;
  try {
    const uri = new URL(value);
    if (uri.protocol !== "https:" || !uri.hostname || uri.username || uri.password || uri.search || uri.hash) {
      return "Use an HTTPS base URI without credentials, a query string, or a fragment.";
    }
  } catch {
    return "Enter a valid HTTPS base URI.";
  }
  return null;
};

const HTTPAPIConnectionPanel = ({ value, onChange }) => {
  const id = useId();
  const [localDraft, setLocalDraft] = useState(EMPTY_HTTP_API_DRAFT);
  const draft = value || localDraft;
  const setDraft = onChange || setLocalDraft;
  const { baseURI, schema, format, convertedDraft } = draft;
  const [documentTab, setDocumentTab] = useState("schema");
  const [fileError, setFileError] = useState(null);
  const [isReading, setIsReading] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [conversionError, setConversionError] = useState(null);
  const [conversionStatus, setConversionStatus] = useState("");
  const readRevision = useRef(0);
  const conversionRevision = useRef(0);
  const conversionController = useRef(null);
  const uriError = getURIError(baseURI);
  const schemaTooLarge = new Blob([schema]).size > MAX_SCHEMA_FILE_SIZE;
  let invalidJSON = false;
  if (format === "json" && schema.trim()) {
    try {
      JSON.parse(schema);
    } catch {
      invalidJSON = true;
    }
  }
  const canConvert = Boolean(
    baseURI.trim() && schema.trim() && !uriError && !schemaTooLarge
    && !invalidJSON && !isReading && !isConverting
  );

  useEffect(() => () => {
    readRevision.current += 1;
    conversionRevision.current += 1;
    conversionController.current?.abort();
  }, []);

  const invalidateConversion = () => {
    conversionRevision.current += 1;
    conversionController.current?.abort();
    conversionController.current = null;
    setIsConverting(false);
    setDraft((current) => ({ ...current, convertedDraft: null }));
    setConversionError(null);
    setConversionStatus(
      convertedDraft || isConverting ? "Inputs changed. Convert the current schema again." : ""
    );
  };

  const handleConvert = async () => {
    if (!canConvert) return;
    const revision = ++conversionRevision.current;
    const controller = new AbortController();
    conversionController.current = controller;
    setIsConverting(true);
    setConversionError(null);
    setConversionStatus("");
    setDraft((current) => ({ ...current, convertedDraft: null }));
    try {
      const result = await convertHTTPAPISchema(
        { base_uri: baseURI.trim(), schema, format },
        { signal: controller.signal },
      );
      if (revision !== conversionRevision.current || controller.signal.aborted) return;
      setDraft((current) => ({ ...current, convertedDraft: result }));
      setDocumentTab("tools");
      setConversionStatus(`${result.tools.length} MCP tools generated. Discover Tools is now available.`);
    } catch (error) {
      if (revision !== conversionRevision.current || controller.signal.aborted) return;
      const message = error.response?.data?.error;
      setConversionError(
        typeof message === "string" ? message : "Schema conversion failed. Please try again."
      );
    } finally {
      if (revision === conversionRevision.current) {
        setIsConverting(false);
        conversionController.current = null;
      }
    }
  };

  const handleSchemaChange = (content) => {
    invalidateConversion();
    readRevision.current += 1;
    setIsReading(false);
    setFileError(null);
    setDraft((current) => ({ ...current, schema: content }));
  };

  const handleFileSelected = async (file) => {
    const revision = ++readRevision.current;
    setFileError(null);
    setIsReading(false);
    if (!file) {
      invalidateConversion();
      setDraft((current) => ({ ...current, schema: "" }));
      return;
    }
    if (!/\.(json|ya?ml)$/i.test(file.name)) {
      setFileError("Choose a JSON or YAML schema file.");
      return;
    }
    if (file.size > MAX_SCHEMA_FILE_SIZE) {
      setFileError("Schema files must be 1 MiB or smaller.");
      return;
    }
    invalidateConversion();
    setIsReading(true);
    try {
      const content = await file.text();
      if (revision !== readRevision.current) return;
      setDraft((current) => ({
        ...current,
        format: /\.json$/i.test(file.name) ? "json" : "yaml",
        schema: content,
      }));
    } catch {
      if (revision !== readRevision.current) return;
      logger.error("Failed to read the HTTP API schema file");
      setFileError("Could not read the schema file. Try again or paste its contents into the editor.");
    } finally {
      if (revision === readRevision.current) setIsReading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor={`${id}-uri`} className={LABEL_CLASS}>Base URI</label>
        <Input
          id={`${id}-uri`}
          type="text"
          inputMode="url"
          value={baseURI}
          onChange={(event) => {
            invalidateConversion();
            setDraft((current) => ({ ...current, baseURI: event.target.value }));
          }}
          placeholder="https://api.example.com/v1"
          error={Boolean(uriError)}
          aria-describedby={uriError ? `${id}-uri-error` : undefined}
        />
        {uriError && <p id={`${id}-uri-error`} className="text-xs text-ink-red-500 mt-1">{uriError}</p>}
      </div>
      <Tabs
        options={DOCUMENT_TABS}
        value={documentTab}
        onChange={setDocumentTab}
        label="HTTP API documents"
        idPrefix={id}
      />
      {fileError && <AlertBanner>{fileError}</AlertBanner>}
      {schemaTooLarge && <AlertBanner>Schema text must be 1 MiB or smaller.</AlertBanner>}
      {conversionError && <AlertBanner>{conversionError}</AlertBanner>}
      {conversionStatus && <p role="status" aria-live="polite" className="text-xs text-ink-gray-600 dark:text-ink-gray-300">{conversionStatus}</p>}
      <div
        role="tabpanel"
        id={`${id}-schema-panel`}
        aria-labelledby={`${id}-schema-tab`}
        hidden={documentTab !== "schema"}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <p className={LABEL_CLASS}>Schema format</p>
            <Select
              value={format}
              onChange={(value) => {
                invalidateConversion();
                setDraft((current) => ({ ...current, format: value }));
              }}
              options={FORMATS}
              placeholder="Schema format"
            />
          </div>
          <div>
            <p className={LABEL_CLASS}>OpenAPI / Swagger file</p>
            <FileUploadInput
              inputLabel="Choose API schema file"
              deferUpload
              accept=".json,.yaml,.yml"
              placeholder="Choose or drop a JSON/YAML schema (local only)"
              onFileSelected={handleFileSelected}
            />
          </div>
        </div>
        {isReading && (
          <div aria-live="polite" className="flex items-center gap-2 text-xs text-ink-gray-500">
            <InlineSpinner size="xs" /> Reading schema...
          </div>
        )}
        <div>
          <label htmlFor={`${id}-schema`} className={LABEL_CLASS}>OpenAPI / Swagger schema</label>
          <JsonView
            id={`${id}-schema`}
            value={schema}
            onChange={handleSchemaChange}
            fileType="schema"
            language={format}
            placeholder="Paste an OpenAPI/Swagger schema in JSON or YAML."
          />
          <p className="text-[11px] text-ink-gray-400 mt-1">
            Conversion supports a bounded subset of OpenAPI 3.0/3.1 and Swagger 2.0.
            Unsupported features are reported explicitly. The entered base URI overrides schema server/host/basePath declarations.
          </p>
        </div>
      </div>
      <div
        role="tabpanel"
        id={`${id}-tools-panel`}
        aria-labelledby={`${id}-tools-tab`}
        hidden={documentTab !== "tools"}
      >
        <JsonView
          value={convertedDraft ? JSON.stringify({ tools: convertedDraft.tools }, null, 2) : ""}
          fileType="mcp-schema"
          readOnly
          ariaLabel="Generated MCP tool schemas"
          placeholder="Convert a schema to inspect its generated MCP tool definitions here."
        />
        <p className="text-[11px] text-ink-gray-400 mt-1">
          {convertedDraft
            ? "Generated tools are read-only in this step. The matching HTTP mappings are retained in the local draft; editing, validation, and saving come next."
            : "No tools have been generated. Enter a base URI and schema, then convert."}
        </p>
      </div>
      <div className="flex flex-wrap gap-3">
        <CompactButton
          type="button"
          disabled={!canConvert}
          onClick={handleConvert}
          className="disabled:cursor-not-allowed disabled:opacity-50"
          title={canConvert ? "Convert this schema into draft MCP tools and HTTP mappings" : "Enter a valid HTTPS base URI and schema to convert"}
        >
          {isConverting ? <InlineSpinner size="xs" /> : <RefreshCw className="h-3.5 w-3.5" />}
          {isConverting ? "Converting" : "Convert to MCP Tools"}
        </CompactButton>
      </div>
    </div>
  );
};

export default HTTPAPIConnectionPanel;
